import React, { useMemo, useState } from 'react';
import { Database, RefreshCw, AlertTriangle, AlertCircle, Info, CheckCircle2 } from 'lucide-react';
import { Card } from '../../ui/Card';
import { Badge } from '../../ui/Badge';
import { Button } from '../../ui/Button';
import { useQualityWorkspaceState } from './useQualityWorkspaceState';
export function SeedAudit({
  refreshKey = 0
}: {
  refreshKey?: number;
}) {
  const [auditRunKey, setAuditRunKey] = useState(0);
  const {
    filteredRecords
  } = useQualityWorkspaceState();
  const rows = useMemo(() => filteredRecords.map(record => ({
    ...record,
    issues: record.issues.filter(i => i.category === 'seeds')
  })).filter(record => record.issues.length > 0), [filteredRecords, refreshKey, auditRunKey]);
  const criticalCount = rows.reduce((sum, r) => sum + r.issues.filter(i => i.severity === 'critical').length, 0);
  const warningCount = rows.reduce((sum, r) => sum + r.issues.filter(i => i.severity === 'warning').length, 0);
  return <div className="space-y-4" data-id="element-2865">
      <Card className="p-5" data-id="element-2866">
        <div className="flex items-center justify-between" data-id="element-2867">
          <div className="flex items-center gap-2" data-id="element-2868">
            <div className="p-2 bg-emerald-50 rounded-lg" data-id="element-2869">
              <Database className="w-5 h-5 text-emerald-600" data-id="element-2870" />
            </div>
            <div data-id="element-2871">
              <h3 className="text-lg font-bold text-slate-900" data-id="element-2872">Seed Audit</h3>
              <p className="text-sm text-slate-500" data-id="element-2873">
                {rows.length} questions have seed-level issues
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2" data-id="element-2874">
            {criticalCount > 0 && <Badge variant="danger" size="sm" data-id="element-2875">
                {criticalCount} critical
              </Badge>}
            {warningCount > 0 && <Badge variant="warning" size="sm" data-id="element-2876">
                {warningCount} warnings
              </Badge>}
            {rows.length === 0 && <Badge variant="success" size="sm" data-id="element-2877">
                All passing
              </Badge>}
            <Button size="sm" variant="secondary" onClick={() => setAuditRunKey(v => v + 1)} data-id="element-2878">
              <RefreshCw className="w-3.5 h-3.5 mr-1.5" data-id="element-2879" />
              Re-run
            </Button>
          </div>
        </div>
      </Card>

      {rows.length === 0 ? <Card className="p-12 text-center" data-id="element-2880">
          <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto mb-3" data-id="element-2881" />
          <h3 className="text-lg font-bold text-slate-900" data-id="element-2882">No seed issues</h3>
          <p className="text-slate-500" data-id="element-2883">
            All seeds pass integrity checks in the current filter scope.
          </p>
        </Card> : <div className="space-y-3" data-id="element-2884">
          {rows.slice(0, 100).map(record => {
        const hasCritical = record.issues.some(i => i.severity === 'critical');
        return <Card key={record.question.id} className={`p-4 border-l-4 ${hasCritical ? 'border-l-rose-500' : 'border-l-amber-400'}`} data-id="element-2885">
                <div className="flex items-center justify-between mb-3" data-id="element-2886">
                  <code className="text-sm font-bold text-slate-900 bg-slate-100 px-2 py-0.5 rounded" data-id="element-2887">
                    {record.question.id}
                  </code>
                  <div className="flex gap-1.5" data-id="element-2888">
                    <Badge variant="neutral" size="sm" data-id="element-2889">
                      {record.question.module}
                    </Badge>
                    <Badge variant="neutral" size="sm" data-id="element-2890">
                      G{record.question.group}
                    </Badge>
                  </div>
                </div>
                <ul className="space-y-1.5" data-id="element-2891">
                  {record.issues.map(issue => <li key={issue.id} className="flex items-start gap-2 text-sm" data-id="element-2892">
                      <SeverityIcon severity={issue.severity} data-id="element-2893" />
                      <span className={issue.severity === 'critical' ? 'text-rose-700' : issue.severity === 'warning' ? 'text-amber-700' : 'text-slate-600'} data-id="element-2894">
                        {issue.message}
                      </span>
                    </li>)}
                </ul>
              </Card>;
      })}
          {rows.length > 100 && <p className="text-center text-sm text-slate-500 py-2" data-id="element-2895">
              Showing 100 of {rows.length} — use filters to narrow results.
            </p>}
        </div>}
    </div>;
}
function SeverityIcon({
  severity
}: {
  severity: string;
}) {
  if (severity === 'critical') return <AlertTriangle className="w-4 h-4 text-rose-500 shrink-0 mt-0.5" data-id="element-2896" />;
  if (severity === 'warning') return <AlertCircle className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" data-id="element-2897" />;
  return <Info className="w-4 h-4 text-blue-400 shrink-0 mt-0.5" data-id="element-2898" />;
}